import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { getSingleShipmentLoadItem } from "../../features/loadList/loadListSlice";
import SmallSpinner from "../shared/spinners/SmallSpinner";

const HistoryContainer = styled.section`
  flex: 0 0 calc(100% - 40px);
  width: calc(100% - 40px);
  margin: 6px 20px 38px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.mainBorderColor};
  cursor: default;

  .history-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }

  .history-title {
    margin: 0;
    color: var(--color-main);
    font-size: 13px;
    font-weight: var(--font-weight-xl);
  }

  .history-count {
    padding: 2px 8px;
    border-radius: 20px;
    background: rgba(232, 84, 70, 0.1);
    color: var(--color-main);
    font-size: 11px;
    font-weight: var(--font-weight-lg);
  }

  .history-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .history-item {
    display: grid;
    grid-template-columns: minmax(120px, 1.1fr) minmax(125px, 1fr) minmax(145px, 1.2fr);
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 9px 11px;
    border: 1px solid ${({ theme }) => theme.mainBorderColor};
    border-radius: 7px;
    font-size: 12px;
    line-height: 1.35;
  }

  .driver,
  .release {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 2px;
  }

  .driver strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .secondary {
    color: ${({ theme }) => theme.secondaryTextColor};
    font-size: 10px;
    overflow-wrap: anywhere;
  }

  .active-badge {
    width: fit-content;
    padding: 3px 7px;
    border-radius: 20px;
    background: rgba(232, 84, 70, 0.1);
    color: var(--color-main);
    font-size: 10px;
    font-weight: var(--font-weight-xl);
  }

  .status {
    min-height: 34px;
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.secondaryTextColor};
    font-size: 12px;
  }

  @media (max-width: 1500px) {
    .history-list {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 1150px) {
    margin-bottom: 66px;

    .history-item {
      grid-template-columns: 1fr;
      gap: 5px;
    }
  }
`;

const OperationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 3px;
  padding: 7px 9px;
  border-left: 2px solid var(--color-main);
  border-radius: 0 5px 5px 0;
  background: rgba(232, 84, 70, 0.06);
  font-size: 11px;
  line-height: 1.35;

  .operation-line {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr);
    gap: 6px;
  }

  .operation-label {
    color: ${({ theme }) => theme.secondaryTextColor};
  }

  .operation-value {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .operation-date {
    color: ${({ theme }) => theme.secondaryTextColor};
  }
`;

export const unwrapTrackingCollection = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.$values)) return value.$values;
  return [];
};

const readValue = (object, camelCaseKey, pascalCaseKey) =>
  object?.[camelCaseKey] ?? object?.[pascalCaseKey] ?? null;

const formatUtcDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const findDetailedRoutePoint = (routePoint, detailedRoutePoints) => {
  if (!routePoint) return null;

  const routePointId = readValue(routePoint, "id", "Id");
  const routePointOrder = readValue(routePoint, "order", "Order");
  const routeType = readValue(routePoint, "loadOrUnload", "LoadOrUnload");

  return (
    detailedRoutePoints.find((candidate) => {
      const candidateId = readValue(candidate, "id", "Id");
      return routePointId != null && candidateId != null && candidateId === routePointId;
    }) ||
    detailedRoutePoints.find(
      (candidate) =>
        readValue(candidate, "order", "Order") === routePointOrder &&
        readValue(candidate, "loadOrUnload", "LoadOrUnload") === routeType
    ) ||
    null
  );
};

const getOperationActor = (routePoint, prefix, dateKey) => {
  const pascalPrefix = `${prefix.charAt(0).toUpperCase()}${prefix.slice(1)}`;
  const pascalDateKey = `${dateKey.charAt(0).toUpperCase()}${dateKey.slice(1)}`;
  const name = readValue(
    routePoint,
    `${prefix}ByUserName`,
    `${pascalPrefix}ByUserName`
  );
  const phone = readValue(
    routePoint,
    `${prefix}ByUserPhoneNumber`,
    `${pascalPrefix}ByUserPhoneNumber`
  );
  const company = readValue(
    routePoint,
    `${prefix}ByCompany`,
    `${pascalPrefix}ByCompany`
  );
  const userId = readValue(
    routePoint,
    `${prefix}ByUserId`,
    `${pascalPrefix}ByUserId`
  );
  const date = formatUtcDate(readValue(routePoint, dateKey, pascalDateKey));

  if (!name && !phone && !company && !userId && !date) return null;

  return {
    identity: [name || (!phone && userId), phone, company].filter(Boolean).join(" · "),
    date,
  };
};

export const RoutePointOperationInfo = ({ routePoint }) => {
  const started = getOperationActor(routePoint, "started", "routePointStartDate");
  const finished = getOperationActor(routePoint, "updated", "routePointEndDate");

  if (!started && !finished) return null;

  return (
    <OperationContainer>
      {started && (
        <div className="operation-line">
          <span className="operation-label">Başlatan</span>
          <span className="operation-value">
            {started.identity || "Bilinmeyen kullanıcı"}
            {started.date && <span className="operation-date"> · {started.date}</span>}
          </span>
        </div>
      )}
      {finished && (
        <div className="operation-line">
          <span className="operation-label">Bitiren</span>
          <span className="operation-value">
            {finished.identity || "Bilinmeyen kullanıcı"}
            {finished.date && <span className="operation-date"> · {finished.date}</span>}
          </span>
        </div>
      )}
    </OperationContainer>
  );
};

const ShipmentTrackingDetails = ({ shipmentId, className }) => {
  const dispatch = useDispatch();
  const [shipment, setShipment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setError(null);

    dispatch(getSingleShipmentLoadItem(shipmentId))
      .unwrap()
      .then((data) => {
        if (isMounted) setShipment(data);
      })
      .catch((requestError) => {
        if (isMounted) {
          setShipment(null);
          setError(requestError?.message || "Sevkiyat takip bilgileri alınamadı.");
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch, shipmentId]);

  const vehicleDriverHistory = unwrapTrackingCollection(
    shipment?.vehicleDriverHistory
  );

  return (
    <HistoryContainer className={className} onClick={(event) => event.stopPropagation()}>
      <div className="history-header">
        <h3 className="history-title">Taşıma Seti Geçmişi</h3>
        {!isLoading && !error && vehicleDriverHistory.length > 0 && (
          <span className="history-count">{vehicleDriverHistory.length} sürücü</span>
        )}
      </div>

      {isLoading ? (
        <div className="status">
          <SmallSpinner size="20px" color="#fff" bgColor="#E85446" />
        </div>
      ) : error ? (
        <div className="status">{error}</div>
      ) : vehicleDriverHistory.length === 0 ? (
        <div className="status">Henüz taşıma seti teslim alma kaydı bulunmuyor.</div>
      ) : (
        <div className="history-list">
          {vehicleDriverHistory.map((session) => {
            const id = readValue(session, "id", "Id");
            const driverUserId = readValue(session, "driverUserId", "DriverUserId");
            const driverName = readValue(session, "driverName", "DriverName");
            const driverPhoneNumber = readValue(
              session,
              "driverPhoneNumber",
              "DriverPhoneNumber"
            );
            const claimedAtUtc = readValue(
              session,
              "claimedAtUtc",
              "ClaimedAtUtc"
            );
            const releasedAtUtc = readValue(
              session,
              "releasedAtUtc",
              "ReleasedAtUtc"
            );
            const releaseReason = readValue(
              session,
              "releaseReason",
              "ReleaseReason"
            );

            return (
              <div className="history-item" key={id ?? `${driverUserId}-${claimedAtUtc}`}>
                <div className="driver">
                  <strong>
                    {driverName || driverPhoneNumber || driverUserId || "Bilinmeyen sürücü"}
                  </strong>
                  {driverName && driverPhoneNumber && (
                    <span className="secondary">{driverPhoneNumber}</span>
                  )}
                </div>
                <div>
                  <span className="secondary">Devralma</span>
                  <div>{formatUtcDate(claimedAtUtc) || "-"}</div>
                </div>
                <div className="release">
                  {releasedAtUtc ? (
                    <>
                      <span className="secondary">Bırakma</span>
                      <span>{formatUtcDate(releasedAtUtc) || "-"}</span>
                      {releaseReason && (
                        <span className="secondary">{releaseReason}</span>
                      )}
                    </>
                  ) : (
                    <span className="active-badge">Set halen sürücüde</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </HistoryContainer>
  );
};

export default ShipmentTrackingDetails;
